import { useRef } from 'react';
import { Modal, Space, Button, Spin, Result } from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Report Viewer Modal.
//
// A contained, in-app viewer for the self-contained HTML documents the Jasper
// report-service returns (rework slip, piece stickers, …). Mirrors the
// WorkOrderCopyPage viewer (sandboxed iframe + scrollable wrapper + a Print
// button that drives the iframe's own print), but as a Modal so it can open
// over a drawer/grid without losing context. The HTML is Jasper-rendered — this
// component only frames it; it never builds markup itself.
//
// Props:
//   open      — modal visibility
//   title     — header text
//   html      — the report HTML string (null while loading / on error)
//   loading   — true while the HTML is being fetched
//   error     — error message string (null when fine)
//   width     — modal width (default '92vw'; pass a number for narrow labels)
//   onReload  — optional; shows a Reload button that re-renders the report
//   onClose   — close handler
// ─────────────────────────────────────────────────────────────────
export default function ReportViewerModal({
  open, title, html, loading = false, error = null,
  width = '92vw', onReload, onClose
}) {
  const frameRef = useRef(null);

  const handlePrint = () => {
    const win = frameRef.current && frameRef.current.contentWindow;
    if (win) { win.focus(); win.print(); }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={width}
      style={{ top: 24 }}
      destroyOnClose
      maskClosable={false}
      styles={{ body: { padding: 0, height: '80vh' } }}
      footer={
        <Space>
          <Button onClick={onClose}>Close</Button>
          {onReload ? <Button icon={<ReloadOutlined />} onClick={onReload} loading={loading}>Reload</Button> : null}
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!html}>Print</Button>
        </Space>
      }
    >
      <div style={{ width: '100%', height: '100%', overflow: 'auto', background: '#f0f2f5', borderTop: '1px solid #f0f0f0' }}>
        {loading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="Rendering…" />
          </div>
        ) : error ? (
          <div style={{ padding: 24 }}>
            <Result
              status="error"
              title="Could not render the report"
              subTitle={error}
              extra={onReload ? <Button type="primary" onClick={onReload}>Retry</Button> : null}
            />
          </div>
        ) : html ? (
          <iframe
            ref={frameRef}
            title={typeof title === 'string' ? title : 'Report'}
            srcDoc={html}
            sandbox="allow-same-origin allow-modals"
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        ) : null}
      </div>
    </Modal>
  );
}
